/**
 * ToPeak Shop All Hero — accessible slide carousel (Figma 324:1416, 951:2764, 951:2774)
 */
(function initTopeakShopAllHero() {
  const MIN_INTERVAL_MS = 5000;

  /**
   * @param {HTMLElement} element
   */
  function applyPointerCursor(element) {
    if (element instanceof HTMLElement) {
      element.style.setProperty('cursor', 'pointer', 'important');
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function enforcePointerCursor(root) {
    applyPointerCursor(root);

    const sectionWrapper = root.closest('.topeak-shop-all-hero-section');
    if (sectionWrapper instanceof HTMLElement) {
      applyPointerCursor(sectionWrapper);
    }

    root.querySelectorAll('*').forEach((element) => {
      applyPointerCursor(element);
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function initHero(root) {
    if (root.dataset.topeakShopAllHeroInit === 'true') {
      return;
    }

    root.dataset.topeakShopAllHeroInit = 'true';
    enforcePointerCursor(root);

    root.addEventListener(
      'dragstart',
      (event) => {
        event.preventDefault();
      },
      true
    );

    const slidesContainer = root.querySelector('.topeak-shop-all-hero__slides');
    const slides = Array.from(root.querySelectorAll('.topeak-shop-all-hero__slide'));
    const dots = Array.from(root.querySelectorAll('.topeak-shop-all-hero__indicator'));

    if (slides.length <= 1 || !(slidesContainer instanceof HTMLElement)) {
      return;
    }

    let index = slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (index < 0) {
      index = 0;
    }

    /** @type {ReturnType<typeof setInterval> | null} */
    let timer = null;
    let manualPaused = false;

    const autoplayEnabled = root.dataset.autoplay !== 'false';
    const intervalSeconds = Number.parseInt(root.dataset.autoplayInterval || '6', 10);
    const intervalMs = Math.max(MIN_INTERVAL_MS, intervalSeconds * 1000);
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    /**
     * @param {number} nextIndex
     */
    function setSlide(nextIndex) {
      index = (nextIndex + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      const activeHeading = slides[index].querySelector('[id^="topeak-shop-all-hero-heading-"]');
      if (activeHeading) {
        root.setAttribute('aria-labelledby', activeHeading.id);
      }
    }

    function stopTimer() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function isAutoplayBlocked() {
      return (
        !autoplayEnabled ||
        manualPaused ||
        reducedMotionQuery.matches ||
        document.hidden ||
        root.matches(':hover') ||
        root.matches(':focus-within')
      );
    }

    function updateAriaLive() {
      root.setAttribute('aria-live', isAutoplayBlocked() ? 'polite' : 'off');
    }

    function startTimer() {
      stopTimer();

      if (isAutoplayBlocked()) {
        return;
      }

      timer = setInterval(() => {
        if (isAutoplayBlocked()) {
          return;
        }

        setSlide(index + 1);
      }, intervalMs);
    }

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const target = Number.parseInt(dot.dataset.slideTo || '', 10);

        if (Number.isNaN(target)) {
          return;
        }

        setSlide(target);
        manualPaused = true;
        stopTimer();
        updateAriaLive();
      });
    });

    root.addEventListener('mouseenter', () => {
      stopTimer();
      updateAriaLive();
    });

    root.addEventListener('mouseleave', () => {
      updateAriaLive();
      startTimer();
    });

    root.addEventListener('focusin', () => {
      stopTimer();
      updateAriaLive();
    });

    root.addEventListener('focusout', (event) => {
      if (event.relatedTarget instanceof Node && root.contains(event.relatedTarget)) {
        return;
      }

      updateAriaLive();
      startTimer();
    });

    const DRAG_ACTIVATION_PX = 10;
    const DRAG_THRESHOLD_PX = 48;
    const DRAG_VELOCITY_THRESHOLD = 0.4;

    let dragPointerId = null;
    let dragStartX = 0;
    let dragStartTime = 0;
    let isDragging = false;

    /**
     * @param {PointerEvent} event
     */
    function isDragExcludedTarget(event) {
      if (!(event.target instanceof Element)) {
        return true;
      }

      return Boolean(event.target.closest('button, a, input, textarea, select, label'));
    }

    /**
     * @param {PointerEvent} event
     */
    function handlePointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      if (isDragExcludedTarget(event)) {
        return;
      }

      dragPointerId = event.pointerId;
      dragStartX = event.clientX;
      dragStartTime = performance.now();
      isDragging = false;

      const controller = new AbortController();
      const { signal } = controller;

      /**
       * @param {PointerEvent} moveEvent
       */
      const onPointerMove = (moveEvent) => {
        if (moveEvent.pointerId !== dragPointerId) {
          return;
        }

        const deltaX = moveEvent.clientX - dragStartX;

        if (!isDragging && Math.abs(deltaX) < DRAG_ACTIVATION_PX) {
          return;
        }

        if (!isDragging) {
          isDragging = true;
          root.classList.add('is-dragging');
          document.documentElement.classList.add('topeak-shop-all-hero-dragging');
          stopTimer();
          updateAriaLive();

          document.addEventListener(
            'click',
            (clickEvent) => {
              clickEvent.preventDefault();
              clickEvent.stopPropagation();
            },
            { capture: true, once: true }
          );
        }

        moveEvent.preventDefault();
      };

      /**
       * @param {PointerEvent} endEvent
       */
      const onPointerEnd = (endEvent) => {
        if (endEvent.pointerId !== dragPointerId) {
          return;
        }

        controller.abort();

        const deltaX = endEvent.clientX - dragStartX;
        const elapsedMs = Math.max(performance.now() - dragStartTime, 1);
        const velocity = deltaX / elapsedMs;

        root.classList.remove('is-dragging');
        document.documentElement.classList.remove('topeak-shop-all-hero-dragging');
        dragPointerId = null;

        if (!isDragging) {
          return;
        }

        isDragging = false;

        const threshold = Math.min(DRAG_THRESHOLD_PX, slidesContainer.offsetWidth * 0.12);
        const passedDistance = Math.abs(deltaX) >= threshold;
        const passedVelocity = Math.abs(velocity) >= DRAG_VELOCITY_THRESHOLD;

        if (passedDistance || passedVelocity) {
          const goNext = passedVelocity ? velocity < 0 : deltaX < 0;

          if (goNext) {
            setSlide(index + 1);
          } else {
            setSlide(index - 1);
          }
        }

        updateAriaLive();
        startTimer();
      };

      document.addEventListener('pointermove', onPointerMove, { signal });
      document.addEventListener('pointerup', onPointerEnd, { signal });
      document.addEventListener('pointercancel', onPointerEnd, { signal });
    }

    slidesContainer.addEventListener('pointerdown', handlePointerDown);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopTimer();
        return;
      }

      updateAriaLive();
      startTimer();
    });

    reducedMotionQuery.addEventListener('change', () => {
      updateAriaLive();
      startTimer();
    });

    setSlide(index);
    updateAriaLive();
    startTimer();
  }

  function boot(container = document) {
    container.querySelectorAll('[data-topeak-shop-all-hero]').forEach((root) => {
      if (root instanceof HTMLElement) {
        initHero(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) {
      boot(event.target);
    }
  });
})();
